// components/Layout.jsx
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  FiMenu, FiX, FiSun, FiMoon, FiLogOut, FiUser, 
  FiKey, FiHome, FiBook, FiSettings, FiShield,
  FiCode, FiUsers
} from 'react-icons/fi';

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isDeveloper = user?.role === 'developer';

  // Navigation items based on role
  const getNavItems = () => {
    if (isAdmin) {
      return [
        { path: '/admin/dashboard', icon: <FiHome />, label: 'Dashboard' },
        { path: '/admin/users', icon: <FiUsers />, label: 'Users' },
        { path: '/admin/api-keys', icon: <FiKey />, label: 'API Keys' },
        { path: '/admin/api-docs', icon: <FiBook />, label: 'API Docs' },
        { path: '/admin/profile', icon: <FiUser />, label: 'Profile' },
        { path: '/admin/settings', icon: <FiSettings />, label: 'Settings' },
      ];
    }
    
    // Developer
    return [
      { path: '/developer/dashboard', icon: <FiHome />, label: 'Dashboard' },
      { path: '/developer/api-keys', icon: <FiKey />, label: 'API Keys' },
      { path: '/developer/api-docs', icon: <FiBook />, label: 'API Docs' },
      { path: '/developer/profile', icon: <FiUser />, label: 'Profile' },
      { path: '/developer/settings', icon: <FiSettings />, label: 'Settings' },
    ];
  };

  const navItems = getNavItems();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  // Get base path for active class
  const basePath = isAdmin ? '/admin' : '/developer';

  return (
    <div className="layout">
      {/* Overlay */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={closeSidebar} />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span className="logo">🚚 Delivery7</span>
          <span className="version">v1.0</span>
        </div>
        
        <div style={{ 
          padding: '4px 12px', 
          marginBottom: '12px',
          fontSize: '11px',
          fontWeight: '600',
          color: isAdmin ? '#ff4757' : '#667eea',
          background: isAdmin ? 'rgba(255,71,87,0.1)' : 'rgba(102,126,234,0.1)',
          borderRadius: '20px',
          display: 'inline-block',
          alignSelf: 'flex-start'
        }}>
          {isAdmin ? '🔒 Admin' : '💻 Developer'}
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeSidebar}
            >
              <span className="icon">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.fullName?.[0] || user?.username?.[0] || 'U'}
            </div>
            <div>
              <div className="user-name">{user?.fullName || user?.username}</div>
              <div className="user-email">{user?.email}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <FiLogOut /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="topbar">
          <div className="topbar-left">
            <button className="menu-toggle" onClick={toggleSidebar}>
              {sidebarOpen ? <FiX /> : <FiMenu />}
            </button>
            <h2>
              {isAdmin ? '🔒 Admin Portal' : '💻 Developer Portal'}
            </h2>
          </div>
          <div className="topbar-right">
            <button className="theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? <FiSun /> : <FiMoon />}
            </button>
            {isAdmin && (
              <span className="admin-badge">Admin</span>
            )}
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {user?.username}
            </span>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export default Layout;